import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import {
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form1099DIVInstructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="IRS Form 1099-DIV instructions for Tax Year 2025"
          description="Get step-by-step guidance for completing Form 1099-DIV for Tax Year 2025. Report dividends and distributions correctly with expert instructions from TaxZerone."
          keywords=""
          canonical="resources/information-returns/form-1099-div-instructions/"
          ogType="article"
          jsonLdPricing="2.49"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="57448"
        />
      }
    >
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 1099-DIV</span>
          </span>{' '}
          Instructions
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/information-returns/form-1099-div-instructions/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-07T09:00:00-05:00"
                      >
                        July 7, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-07T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 7, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Alexia Zepeda
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 1099-DIV at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemProp="articleBody"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Form 1099-DIV{' '}
                        </strong>{' '}
                        is used to report dividends, capital gain distributions,
                        exempt-interest dividends, liquidation distributions,
                        and other stock-related distributions.
                      </span>
                    </li>

                    <li>
                      <span>
                        Recipient copies must be furnished by{' '}
                        <strong style={{ fontWeight: 600 }}> January 31</strong>
                        , and the form must be filed with the IRS by{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          February 28
                        </strong>{' '}
                        if filing on paper or{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          March 31
                        </strong>{' '}
                        if filing electronically.
                      </span>
                    </li>

                    <li>
                      <span>
                        This article covers{' '}
                        <strong style={{ fontWeight: 600 }}>
                          line-by-line instructions
                        </strong>
                        , including payer and recipient details, dividend
                        reporting in{' '}
                        <strong style={{ fontWeight: 600 }}> Boxes 1-2</strong>,
                        other distributions and withholding in{' '}
                        <strong style={{ fontWeight: 600 }}> Boxes 3-12</strong>
                        , and state reporting information in{' '}
                        <strong style={{ fontWeight: 600 }}>Boxes 13-16</strong>
                        .
                      </span>
                    </li>

                    <li>
                      <span>
                        Form 1099-DIV late filing penalties range from{' '}
                        <strong style={{ fontWeight: 600 }}>
                          $60 to $680 per return
                        </strong>
                        , based on the filing delay and intentional disregard of
                        IRS filing requirements.
                      </span>
                    </li>

                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Extensions:
                        </strong>{' '}
                        <Link
                          href="/tax-extension/efile-irs-form-8809-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          File Form 8809
                        </Link>{' '}
                        to request an automatic filing extension.{' '}
                        <Link
                          href="/tax-extension/file-irs-form-15397-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          File Form 15397
                        </Link>{' '}
                        to request the full{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          30-day extension
                        </strong>{' '}
                        for furnishing recipient copies.
                      </span>
                    </li>
                  </Box>

                  {/* Publisher needs an Organization with a logo ImageObject — no meta tags, same pattern */}
                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>
                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What is IRS Form 1099-DIV?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Form 1099-DIV is an information return issued by banks and
                  other financial institutions to report dividends and
                  distributions received by an investor within a calendar year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Investors receive a 1099-div form from each institution where
                  they have investments that paid out dividends or other
                  distributions exceeding $10 during the year. If investors have
                  accounts at multiple institutions, they may receive several
                  forms.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  The information provided on Form 1099-DIV is used by investors
                  to accurately report the dividends and distributions on their
                  tax return.
                </Typography>
                {/* <Alert
                  severity="info"
                  sx={{ mb: 4 }}
                  icon={<TipsAndUpdatesRoundedIcon />}
                >
                  <b>Tip:</b> Make sure you collect W-9 forms from any
                  contractors you pay so you have the necessary information to
                  complete the 1099-NEC accurately.
                </Alert> */}

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Who needs to file Form 1099-DIV?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  As a payer, you must{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-div/"
                    sx={{ color: '#0202ff' }}
                  >
                    file Form 1099-DIV
                  </Link>{' '}
                  for each recipient if any of the following situations occur:
                </Typography>

                <List sx={{ mb: 2 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      You paid dividends totaling $10 or more to a recipient.
                      This includes capital gain dividends, exempt-interest
                      dividends, and other distributions on stock ownership.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      You withheld and paid any foreign tax on dividends or
                      other stock distributions on behalf of a recipient.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      You withheld federal income tax on dividends paid to a
                      recipient, as required by the backup withholding rules.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      You paid $600 or more in money or other property as part
                      of a liquidation.
                    </ListItemText>
                  </ListItem>
                </List>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If any of these conditions apply, you are legally obligated to
                  file Form 1099-DIV to report the dividend payments or
                  distributions made to that recipient during the tax year.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  When is the Form 1099-DIV due date?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Payers must be aware of two important deadlines related to
                  Form 1099-DIV:
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Filing deadline with the IRS
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The deadline for payers to file Form 1099-INT with the IRS
                  depends on the filing method:
                </Typography>

                <ul style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    <strong>Electronic filing deadline: March 31, 2026</strong>
                    <br /> If you are e-filing Form 1099-DIV with the IRS, the
                    deadline is March 31, 2026 of the year following the tax
                    year for which the form is being issued.
                  </li>
                  <Box
                    component="div"
                    sx={{ mb: 2, p: 2, borderRadius: 2 }}
                    className="gradient-bg"
                  >
                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      spacing={2}
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box sx={{ width: '100%' }}>
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 600, mb: 1 }}
                        >
                          Avoid the last-minute 1099-DIV deadline stress
                        </Typography>
                        <Typography variant="body2">
                          E-file with TaxZerone now for easy IRS compliance and
                          peace of mind.
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          width: '100%',
                          maxWidth: 150,
                          textAlign: 'center',
                        }}
                      >
                        <Button
                          variant="contained"
                          sx={{
                            textTransform: 'none',
                            boxShadow: 'none',
                            borderRadius: 2,
                            backgroundColor: '#fff',
                            color: '#333',
                            '&:hover, &:focus, &:active': { color: '#fff' },
                          }}
                          size="large"
                          href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1099divinstructions`}
                        >
                          E-File Now
                        </Button>
                      </Box>
                    </Stack>
                  </Box>
                  <li style={{ marginBottom: 16 }}>
                    <strong>Paper filing deadline: February 28, 2026*</strong>
                    <br /> If you are filing Form 1099-DIV with the IRS using
                    traditional paper methods, the deadline is earlier –
                    February 28, 2026* of the year following the tax year for
                    which the form is being issued.
                  </li>
                </ul>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Recipient copies deadline: January 31, 2026*
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Payers are required to provide copies of Form 1099-DIV to the
                  recipients by January 31st of the year following the tax year
                  for which the form is being issued. This ensures that
                  recipients have the necessary information to accurately report
                  their dividends and distributions when filing their tax
                  returns.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mt: 4, mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Note:</b> * if the due date falls on the weekend or federal
                  holiday, it is extended to next business days.
                </Alert>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Failure to meet these deadlines or providing inaccurate
                  information on Form 1099-DIV may result in penalties imposed
                  by the IRS. Payers should take appropriate measures to ensure
                  timely and accurate filing and distribution of Form 1099-DIV
                  to avoid any potential penalties.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Form 1099-DIV penalty
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you miss filing Form 1099-DIV within the deadline, the IRS
                  will impose a penalty. Here&apos;s the Form 1099-DIV penalty
                  rates:
                </Typography>
                <Box component="div" sx={{ mb: 2 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Days late
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Penalty per return
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>Up to 30 days</TableCell>
                          <TableCell>$60</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>31 days late through August 1</TableCell>
                          <TableCell>$130</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>After August 1 or not filed</TableCell>
                          <TableCell>$340</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Intentional disregard</TableCell>
                          <TableCell>$680</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Stack
                  direction={{ md: 'row', xs: 'column' }}
                  spacing={2}
                  alignItems="center"
                  sx={{
                    mb: 5,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    backgroundSize: {
                      xs: '20% auto',
                      sm: '20% auto',
                      md: '20% auto',
                    },
                    border: '1px solid #ccc',
                  }}
                >
                  <Box sx={{ width: '100%' }}>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        mb: 0.5,
                        fontSize: '20px !important',
                      }}
                    >
                      Why risk penalties?
                    </Typography>
                    <Typography variant="body2">
                      Choose TaxZerone to e-file your 1099-DIV forms ahead of
                      the deadline.
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: '100%',
                      maxWidth: 198,
                      textAlign: 'left',
                    }}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{
                        textTransform: 'none',
                        boxShadow: 'none',
                        borderRadius: 2,
                      }}
                      size="large"
                      href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1099divinstructions`}
                    >
                      E-file Form 1099-DIV
                    </Button>
                  </Box>
                </Stack>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Form 1099-DIV Instructions - How to fill out?
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Let&apos;s see line-by-line instructions on how to fill out
                  Form 1099-DIV.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Payer details
                </Typography>

                <picture>
                  <source srcSet="/assets/images/payer-details-fom-1099-div.webp" />
                  <source srcSet="/assets/images/payer-details-fom-1099-div.png" />
                  <img
                    src="/assets/images/payer-details-fom-1099-div.png"
                    loading="lazy"
                    alt="Form 1099-div-payer details"
                    width={492}
                    height={289}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter your name or business name, complete address, and TIN
                  (SSN if you&apos;re an individual; EIN if you&apos;re a
                  business)
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    mt: 2,
                    fontWeight: 600,
                  }}
                >
                  Recipient details
                </Typography>
                <picture>
                  <source srcSet="/assets/images/recipient-form-1099-div.webp" />
                  <source srcSet="/assets/images/recipient-form-1099-div.png" />
                  <img
                    src="/assets/images/recipient-form-1099-div.png"
                    loading="lazy"
                    alt="Form 1099-div-recipient details"
                    width={495}
                    height={456}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mt: 1, mb: 1 }}>
                  Enter the recipient’s name, complete address, and TIN (SSN for
                  an individual; EIN for a business).
                </Typography>
                <ul style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    <b> Account number:</b> You must provide the account number
                    in these two situations:
                    <ol style={{ marginBottom: 32, listStyleType: 'circle' }}>
                      <li style={{ marginBottom: 16 }}>
                        You have multiple accounts for the same recipient, and
                        you are filing more than one Form 1099-DIV for that
                        recipient.
                      </li>
                      <li style={{ marginBottom: 16 }}>
                        You checked the &quot;FATCA filing requirement&quot;
                        box.
                      </li>
                    </ol>
                  </li>

                  <li style={{ marginBottom: 16 }}>
                    <b> 2nd TIN not:</b> If the IRS has notified you two times
                    within the last 3 years that the payee provided an incorrect
                    TIN, you can enter an &quot;X&quot; in this box. By doing
                    so, the IRS will stop sending you further notices about this
                    account&apos;s TIN.
                  </li>
                </ul>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 1a: Total Ordinary Dividends
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-div-total-ordinary.webp" />
                  <source srcSet="/assets/images/form-1099-div-total-ordinary.png" />
                  <img
                    src="/assets/images/form-1099-div-total-ordinary.png"
                    loading="lazy"
                    alt="Form 1099-div-box-1a-total ordinary dividends"
                    width={207}
                    height={73}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the total amount of ordinary dividends received,
                  including:
                </Typography>
                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 10 }}>
                    Dividends from regular stocks and money market funds
                  </li>
                  <li style={{ marginBottom: 10 }}>
                    Net short-term capital gains from mutual funds
                  </li>
                  <li style={{ marginBottom: 10 }}>
                    Other distributions on stocks
                  </li>
                  <li style={{ marginBottom: 10 }}>Reinvested dividends</li>
                  <li style={{ marginBottom: 10 }}>
                    Section 404(k) dividends paid directly from the corporation
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  The amount in Box 1a includes the amounts reported in Boxes
                  1b, 2e, and the recipient&apos;s share of investment expenses
                  from Box 6.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 1b: Qualified Dividends
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-div-qualified-dividends.webp" />
                  <source srcSet="/assets/images/form-1099-div-qualified-dividends.png" />
                  <img
                    src="/assets/images/form-1099-div-qualified-dividends.png"
                    loading="lazy"
                    alt="    Form 1099-div-box-1b-qualified dividends "
                    width={203}
                    height={74}
                    className="proportional-img"
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the portion of the dividends in box 1a that qualifies
                  for the reduced capital gains rates.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 2a–-2f
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-div-2a-2f.webp" />
                  <source srcSet="/assets/images/form-1099-div-2a-2f.png" />
                  <img
                    src="/assets/images/form-1099-div-2a-2f.png"
                    loading="lazy"
                    alt="Form 1099-div-box-2a-2f"
                    width={405}
                    height={146}
                    className="proportional-img"
                  />
                </picture>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    mt: 3,
                    fontWeight: 600,
                  }}
                >
                  Box 2a: Total Capital Gain Distr.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter total capital gain distributions (long-term). Include
                  all amounts shown in boxes 2b, 2c, 2d, and 2f.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,

                    fontWeight: 600,
                  }}
                >
                  Box 2b: Unrecap. Sec. 1250 Gain.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter any amount included in box 2a that is an unrecaptured
                  section 1250 gain from certain depreciable real property.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,

                    fontWeight: 600,
                  }}
                >
                  Box 2c: Section 1202 Gain
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter any amount included in box 2a that is a section 1202
                  gain from certain qualified small business stock.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,

                    fontWeight: 600,
                  }}
                >
                  Box 2d: Collectibles (28%) Gain
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter any amount included in box 2a that is a 28% rate gain
                  from sales or exchanges of collectibles.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,

                    fontWeight: 600,
                  }}
                >
                  Box 2e: Section 897 Ordinary Dividends
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter any amount included in box 1a that is section 897 gain
                  from dispositions of USRPI.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,

                    fontWeight: 600,
                  }}
                >
                  Box 2f: Section 897 Capital Gain
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter any amount included in box 2a that is section 897 gain
                  from dispositions of USRPI.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> Only RICs and REITs should complete boxes 2e and
                  2f. Boxes 2e and 2f do not need to be completed for recipients
                  that are U.S. individuals.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 3: Nondividend Distributions
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-div-nondividend.webp" />
                  <source srcSet="/assets/images/form-1099-div-nondividend.png" />
                  <img
                    src="/assets/images/form-1099-div-nondividend.png"
                    loading="lazy"
                    alt="Form 1099-div-box-3-nondividend distributions"
                    width={203}
                    height={50}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter non-dividend distributions, if determinable.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 4: Federal Income Tax Withheld
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-div-federal-income.webp" />
                  <source srcSet="/assets/images/form-1099-div-federal-income.png" />
                  <img
                    src="/assets/images/form-1099-div-federal-income.png"
                    loading="lazy"
                    alt="Form 1099-div-box-4-federal income tax withheld"
                    width={203}
                    height={50}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter any federal income tax that was withheld from the
                  payments reported.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> Withholding is required if recipients have not
                  provided their TIN.
                </Alert>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 5: Section 199A Dividends
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-div-199A-dividends.webp" />
                  <source srcSet="/assets/images/form-1099-div-199A-dividends.png" />
                  <img
                    src="/assets/images/form-1099-div-199A-dividends.png"
                    loading="lazy"
                    alt="Form 1099-div-box-5-section 199A dividends"
                    width={203}
                    height={50}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the qualified REIT dividends paid by a REIT or section
                  199A dividends paid by a RIC to the recipient. This amount is
                  included in the amount reported in box 1a.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 6: Investment Expenses
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-div-investment.webp" />
                  <source srcSet="/assets/images/form-1099-div-investment.png" />
                  <img
                    src="/assets/images/form-1099-div-investment.png"
                    loading="lazy"
                    alt="Form 1099-div-box-6-investment expenses"
                    width={203}
                    height={50}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the recipient&apos;s proportional share of certain
                  expenses that a non-publicly offered regulated investment
                  company (RIC) can deduct when computing its taxable income.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This amount must be included in the recipient&apos;s gross
                  income under section 67(c). It should also be added to the
                  amount reported in Box 1a (Total Ordinary Dividends).
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  DO NOT include any investment expenses in Box 1b.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 7: Foreign Tax Paid
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-div-foreign-tax.webp" />
                  <source srcSet="/assets/images/form-1099-div-foreign-tax.png" />
                  <img
                    src="/assets/images/form-1099-div-foreign-tax.png"
                    loading="lazy"
                    alt="Form 1099-div-box-7-foreign tax paid "
                    width={203}
                    height={74}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter foreign tax paid on dividends and other distributions on
                  stock. A RIC must report only the amount it elects to pass
                  through to the recipient. This amount must be reported in U.S.
                  dollars.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 8: Foreign Country or U.S. Possession
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-div-foreign-country.webp" />
                  <source srcSet="/assets/images/form-1099-div-foreign-country.png" />
                  <img
                    src="/assets/images/form-1099-div-foreign-country.png"
                    loading="lazy"
                    alt="Form 1099-div-box-8-foreign country or u.s. possession"
                    width={205}
                    height={74}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the name of the foreign country or U.S. territory for
                  which the foreign tax was paid. Only applicable if you have
                  reported in box 7.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 9: Cash Liquidation Distributions
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-div-cash-liquidation.webp" />
                  <source srcSet="/assets/images/form-1099-div-cash-liquidation.png" />
                  <img
                    src="/assets/images/form-1099-div-cash-liquidation.png"
                    loading="lazy"
                    alt="Form 1099-div-box-9-cash liquidation distributions"
                    width={203}
                    height={50}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter cash distributed as part of a liquidation.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 10: Noncash Liquidation Distributions
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-div-noncash.webp" />
                  <source srcSet="/assets/images/form-1099-div-noncash.png" />
                  <img
                    src="/assets/images/form-1099-div-noncash.png"
                    loading="lazy"
                    alt="Form 1099-div-box-10-noncash liquidation distributions"
                    width={203}
                    height={50}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter noncash distributions made as part of a liquidation.
                  Show the fair market value as of the date of distribution.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 11: FATCA filing requirement checkbox
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-div-fataca-checkbox.webp" />
                  <source srcSet="/assets/images/form-1099-div-fataca-checkbox.png" />
                  <img
                    src="/assets/images/form-1099-div-fataca-checkbox.png"
                    loading="lazy"
                    alt="Form 1099-div-box-11-fatca filing requirement checkbox"
                    width={118}
                    height={74}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Check this box if you&apos;re
                </Typography>
                <ul style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    a U.S. payer reporting on Form(s) 1099, including reporting
                    distributions in boxes 1 through 3 and 9, 10, 12, and 13 on
                    this Form 1099-DIV, to fulfill your obligation for a U.S.
                    account under chapter 4 of the Internal Revenue Code
                    (Regulations section 1.1471-4(d)(2)(iii)(A)).
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    an FFI reporting payments to a U.S. account based on an
                    election in Regulations section 1.1471-4(d)(5)(i)(A).
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 12: Exempt-Interest Dividends
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-div-exempt-interest.webp" />
                  <source srcSet="/assets/images/form-1099-div-exempt-interest.png" />
                  <img
                    src="/assets/images/form-1099-div-exempt-interest.png"
                    loading="lazy"
                    alt="Form 1099-div-box-12- exempt-interest dividends"
                    width={203}
                    height={74}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the amount of exempt-interest dividends received from a
                  mutual fund or other RIC. These are dividends derived from
                  tax-exempt interest income sources.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Also include in Box 12 any specified private activity bond
                  interest dividends, which should additionally be reported
                  separately in Box 13.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 13: Specified Private Activity Bond Interest Dividends
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-div-specified.webp" />
                  <source srcSet="/assets/images/form-1099-div-specified.png" />
                  <img
                    src="/assets/images/form-1099-div-specified.png"
                    loading="lazy"
                    alt="Form 1099-div-box-13-specified private activity bond interest dividends"
                    width={203}
                    height={74}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }} component="div">
                  Report the portion of exempt-interest dividends paid by a
                  regulated investment company (RIC) or mutual fund that relates
                  specifically to interest earned on specified private activity
                  bonds, after subtracting allocated expenses.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 14–16: State information
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-div-state-information.webp" />
                  <source srcSet="/assets/images/form-1099-div-state-information.png" />
                  <img
                    src="/assets/images/form-1099-div-state-information.png"
                    loading="lazy"
                    alt="Form 1099-div-box-14-16-state information"
                    width={406}
                    height={74}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <b> Box 14:</b> Enter the 2-letter state abbreviation for the
                  state
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  <b> Box 15:</b> Enter the state identification number
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <b> Box 16:</b> Report any state income tax withheld from the
                  payment
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to File Form 1099-DIV
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  You can file Form 1099-DIV, which reports dividends and
                  distributions, either electronically or by mail.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Electronic Filing (E-filing)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The IRS recommends e-filing Form 1099-DIV, as it is an
                  efficient and straightforward process. E-filing also offers
                  the advantage of receiving notifications promptly after the
                  IRS processes your submission.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  With TaxZerone , you can{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-div/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    e-file Form 1099-div
                  </Link>{' '}
                  allow you to complete it within minutes. Here’s how it work
                </Typography>

                <ol style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    Fill out the form with the required information.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Review the information for accuracy.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Transmit the form to the IRS electronically.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Provide the recipient&apos;s copy of the form.
                  </li>
                </ol>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  By following these simple steps, you can ensure that your Form
                  1099-DIV is filed electronically in a timely and efficient
                  manner.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Paper filing
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If you prefer to file Form 1099-DIV through traditional paper
                  methods, follow these steps:
                </Typography>
                <ol style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    Download and print Form 1099-DIV from the IRS website.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Complete the required fields, including the recipient&apos;s
                    name, address, and Taxpayer Identification Number (TIN).
                    Also, provide your business name, address, and TIN.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Mail the completed paper form to the IRS at the{' '}
                    <Link
                      href="#1099-div-mailing-address"
                      sx={{ color: '#0202ff' }}
                    >
                      address specified
                    </Link>{' '}
                    in the 1099 instructions.
                  </li>
                  <li>
                    Send a copy of the 1099-DIV to the recipient by January
                    31st, allowing them to report the interest income accurately
                    on their tax return.
                  </li>
                </ol>
                <Typography
                  variant="body1"
                  sx={{ mb: 1, fontWeight: 600, display: 'flex' }}
                >
                  <ReportProblemRoundedIcon
                    sx={{ mr: 1, color: 'rgb(237, 108, 2)' }}
                  />{' '}
                  Important considerations for paper filing:
                </Typography>
                <ul style={{ marginBottom: 40 }}>
                  <li style={{ marginBottom: 16 }}>
                    Mail the forms several weeks before the deadline to ensure
                    timely arrival at the IRS.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    You are required to provide a copy of the 1099-DIV to the
                    recipient for accurate tax reporting on their part.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Paper filing involves printing, mailing, and tracking forms,
                    which can be time-consuming and less efficient compared to
                    electronic filing.
                  </li>
                  <li
                    style={{ marginBottom: 16 }}
                    id="1099-div-mailing-address"
                  >
                    As an alternative, you can choose to file Form 1099-DIV
                    electronically for faster processing and delivery, reducing
                    the risk of delays or lost forms.
                  </li>
                </ul>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Where to send Form 1099-DIV - Mailing address
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you prefer paper filing, the mailing address for Form
                  1099-DIV varies depending on your business location. Below is
                  a table summarizing the mailing address for form 1099-div:
                </Typography>
                <Box component="div" sx={{ mb: 5 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell sx={{ color: '#444', width: 300 }}>
                            If your business operates in or your legal residence
                            is…
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Mail Form 1099-DIV to…
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>
                            Alabama, Arizona, Arkansas, Delaware, Florida,
                            Georgia, Kentucky, Maine, Massachusetts,
                            Mississippi, New Hampshire, New Jersey, New Mexico,
                            New York, North Carolina, Ohio, Texas, Vermont,
                            Virginia
                          </TableCell>
                          <TableCell>
                            Internal Revenue Service
                            <br />
                            Austin Submission Processing Center
                            <br />
                            P.O. Box 149213
                            <br />
                            Austin, TX 78714
                            <br />
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>
                            Alaska, Colorado, Hawaii, Idaho, Illinois, Indiana,
                            Iowa, Kansas, Michigan, Minnesota, Missouri,
                            Montana, Nebraska, Nevada, North Dakota, Oklahoma,
                            Oregon, South Carolina, South Dakota, Tennessee,
                            Utah, Washington, Wisconsin, Wyoming
                          </TableCell>
                          <TableCell>
                            Department of the Treasury
                            <br />
                            IRS Submission Processing Center
                            <br />
                            P.O. Box 219256
                            <br /> Kansas City, MO 64121-9256
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>
                            California, Connecticut, District of Columbia,
                            Louisiana, Maryland, Pennsylvania, Rhode Island,
                            West Virginia
                          </TableCell>
                          <TableCell>
                            Department of the Treasury
                            <br />
                            IRS Submission Processing Center
                            <br />
                            1973 North Rulon White Blvd.
                            <br />
                            Ogden, UT 84201
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Outside the United States</TableCell>
                          <TableCell>
                            Internal Revenue Service,
                            <br /> Austin Submission Processing Center,
                            <br /> P.O. Box 149213, <br />
                            Austin, TX 78714
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to E-File Form 1099-DIV with TaxZerone
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Before initiating the e-filing process for Form 1099-DIV,
                  it&apos;s essential to have all the necessary information
                  ready to ensure a smooth and efficient filing experience.
                  Here&apos;s what you&apos;ll need to gather:
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Required Information for Filing Form 1099-DIV:
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 16 }}>
                    Business Details (name, address, and TIN)
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Recipient Details (name, address, and TIN)
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Dividends and distributions paid
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Federal income tax withheld
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    State income tax withheld
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Once you have collected all the required information, you can
                  proceed with the e-filing process using TaxZerone in three
                  simple steps:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>Step 1: Complete Form 1099-DIV</b>
                  <br />
                  Open the Form 1099-DIV and fill out the necessary fields with
                  the information you have gathered, including the payer&apos;s
                  details, the recipient&apos;s details, dividends paid, and any
                  federal income tax withheld.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>Step 2: Review and Transmit the Return</b>
                  <br />
                  Carefully review all the information you have entered to
                  ensure accuracy. Once you have verified the details, transmit
                  the completed Form 1099-DIV to the IRS electronically through
                  TaxZerone&apos;s secure platform.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>Step 3: Provide the Recipient Copy</b>
                  <br />
                  After successfully transmitting the form to the IRS, send a
                  copy of the completed Form 1099-DIV to the recipient. This
                  will allow them to report the dividend income accurately on
                  their tax return.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Benefits of E-Filing Form 1099-DIV with TaxZerone
                </Typography>

                <Typography variant="body1" sx={{ mb: 0 }}>
                  When you choose to{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-div/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    e-file Form 1099-DIV
                  </Link>{' '}
                  with TaxZerone, you can enjoy several advantages that
                  streamline the filing process and ensure compliance with IRS
                  requirements. These benefits include:
                </Typography>
                <List sx={{ mb: 0 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        IRS Form Validations:
                      </span>{' '}
                      TaxZerone&apos;s system checks your returns for potential
                      errors or missing information, ensuring accuracy before
                      submission.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        Email Recipient Copies:
                      </span>{' '}
                      You can conveniently send recipient copies of Form
                      1099-DIV via email, eliminating the need for manual
                      mailing.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        Secure Portal Access (ZeroneVault):
                      </span>{' '}
                      Recipients can access their return copies anytime,
                      anywhere, through the secure ZeroneVault portal, providing
                      greater convenience and accessibility.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        Competitive Pricing:
                      </span>{' '}
                      TaxZerone offers industry-leading pricing, with rates as
                      low as $0.59 per form, making it an affordable solution
                      for businesses of all sizes.
                    </ListItemText>
                  </ListItem>
                </List>
                <Typography variant="body1">
                  By leveraging TaxZerone&apos;s e-filing solution, you can
                  streamline the form 1099-div filing process, ensuring
                  accuracy, efficiency, and compliance with IRS requirements.
                  You can save time, reduce the risk of errors, and provide a
                  better experience for your recipients.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 30, md: 26, sm: 22, xs: 18 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Take advantage of these benefits and maximize the efficiency of
              your tax reporting by choosing TaxZerone for e-filing Form
              1099-DIV.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1099divinstructions`}
              >
                E-file Form 1099-DIV
              </Button>
              {/* <Typography variant="body2" sx={{ mt: 1 }}>
                Effortless, efficient, and affordable e-filing awaits you.
              </Typography> */}
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form1099DIVInstructions;
